<template>
  <div class="px-5 sm:px-6 lg:px-10 py-6">
    <h1 class="text-[22px] sm:text-[24px] leading-7 font-bold text-gray-900 mb-12">Payments</h1>

    <div v-if="!can.view" class="mt-10"></div>

    <div v-else class="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      <div class="flex justify-center px-5 pt-4 mb-4">
        <div class="flex items-center gap-12">
          <button
            v-if="can.viewPayments"
            class="h-12 px-4 text-lg sm:text-[20px] font-semibold"
            :class="activeMainTab === 'payments' ? 'text-gray-900' : 'text-gray-400'"
            @click="activeMainTab = 'payments'"
          >
            Payments
          </button>
          <button
            v-if="can.viewPaymentBox"
            class="h-12 px-4 text-lg sm:text-[20px] font-semibold"
            :class="activeMainTab === 'paymentBox' ? 'text-gray-900' : 'text-gray-400'"
            @click="activeMainTab = 'paymentBox'"
          >
            Payment box
          </button>
        </div>
      </div>

      <div v-if="activeMainTab === 'payments'">
        <DynamicTable
          :rows="payments"
          :columns="columns"
          :actions="actions"
          server-side
          :total-rows="pagination.total"
          v-model:page="pagination.page"
          :rowsPerPage="pagination.perPage"
          :rowsPerPageOptions="[10, 20, 50]"
          @update:rowsPerPage="onRowsPerPageChange"
          @update:search="onSearchChange"
          :rowClass="rowClass"
          :showAddButton="false"
        >
          <template #tabs></template>

          <template #action="{ row }">
            <div class="flex items-center justify-center gap-2 text-sm text-black">
              <template v-if="can.edit">
                <button
                  @click="editPayment(row)"
                  class="inline-flex items-center gap-2 hover:opacity-80"
                  title="Edit"
                >
                  <svg viewBox="0 0 24 24" class="w-4 h-4" aria-hidden="true">
                    <path
                      d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25Zm18-11.5a1 1 0 0 0 0-1.41L18.66 3a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75L21 5.75Z"
                      fill="currentColor"
                    />
                  </svg>
                  <span>Edit</span>
                </button>
              </template>

              <template v-if="can.delete">
                <button
                  @click="openDeleteModal(row)"
                  class="inline-flex items-center gap-2 hover:opacity-80"
                  title="Delete"
                >
                  <i class="fas fa-trash-alt text-red-600"></i>
                  <span>Delete</span>
                </button>
              </template>

              <template v-if="can.file">
                <button
                  @click="openFile(row)"
                  class="inline-flex items-center gap-2 hover:opacity-80 disabled:opacity-50"
                  :disabled="fileLoadingId === row.id"
                  title="File"
                >
                  <svg
                    v-if="fileLoadingId !== row.id"
                    viewBox="0 0 24 24"
                    class="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <path d="M14 2v6h6" />
                    <path d="M9 13h6M9 17h6M9 9h1" />
                  </svg>
                  <svg
                    v-else
                    class="animate-spin w-5 h-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  <span>File</span>
                </button>
              </template>

              <template v-if="!can.edit && !can.delete && !can.file">
                <span class="text-gray-400">—</span>
              </template>
            </div>
          </template>
          <template #company_name="{ row }">
            <span
              @click="toggleCompanyCol"
              class="cursor-pointer select-none hover:underline"
              :title="showCompanyName ? 'Click to hide' : row.company_name"
            >
              {{ showCompanyName ? row.company_name : row.company_id }}
            </span>
          </template>
        </DynamicTable>
      </div>

      <div v-else class="pb-6">
        <div class="border-t border-b border-gray-300 w-full md:p-6">
          <div class="flex items-center justify-center gap-10 mb-12">
            <button
              class="text-lg font-semibold"
              :class="paymentBoxTab === 'box' ? 'text-gray-900' : 'text-gray-400'"
              @click="switchBoxTab('box')"
            >
              Payment box
            </button>
            <button
              class="text-lg font-semibold"
              :class="paymentBoxTab === 'daily' ? 'text-gray-900' : 'text-gray-300'"
              @click="switchBoxTab('daily')"
            >
              Daily Report
            </button>
          </div>

          <div v-if="can.viewPaymentBox">
            <!-- Payment box tab -->
            <div v-if="paymentBoxTab === 'box'">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-5 mx-auto max-w-3xl">
                <div>
                  <label class="block text-sm font-bold text-gray-700 mb-1">From</label>
                  <input
                    v-model="filters.from"
                    type="date"
                    class="w-full h-10 rounded-xl border-2 px-3 text-sm focus:outline-none"
                    :class="boxErrors.from ? 'border-red-400' : 'border-gray-300'"
                    @change="clearBoxError('from')"
                  />
                  <p v-if="boxErrors.from" class="mt-1 text-xs text-red-500">
                    {{ boxErrors.from }}
                  </p>
                </div>
                <div>
                  <label class="block text-sm font-bold text-gray-700 mb-1">To</label>
                  <input
                    v-model="filters.to"
                    type="date"
                    class="w-full h-10 rounded-xl border-2 px-3 text-sm focus:outline-none"
                    :class="boxErrors.to ? 'border-red-400' : 'border-gray-300'"
                    @change="clearBoxError('to')"
                  />
                  <p v-if="boxErrors.to" class="mt-1 text-xs text-red-500">{{ boxErrors.to }}</p>
                </div>
                <div>
                  <label class="block text-sm font-bold text-gray-700 mb-1">Company</label>
                  <select
                    v-model="filters.company_id"
                    class="w-full h-10 rounded-xl border-2 px-3 text-sm focus:outline-none"
                    :class="boxErrors.company_id ? 'border-red-400' : 'border-gray-300'"
                    @change="clearBoxError('company_id')"
                  >
                    <option value="" disabled>Select company</option>
                    <option v-for="c in companies" :key="c.id" :value="c.id">{{ c.name }}</option>
                  </select>
                  <p v-if="boxErrors.company_id" class="mt-1 text-xs text-red-500">
                    {{ boxErrors.company_id }}
                  </p>
                </div>
                <div>
                  <label class="block text-sm font-bold text-gray-700 mb-1">Employee</label>
                  <select
                    v-model="filters.employee_id"
                    class="w-full h-10 rounded-xl border-2 px-3 text-sm focus:outline-none"
                    :class="boxErrors.employee_id ? 'border-red-400' : 'border-gray-300'"
                    @change="clearBoxError('employee_id')"
                  >
                    <option value="all">All</option>
                    <option v-for="e in employees" :key="e.id" :value="e.id">{{ e.name }}</option>
                  </select>
                  <p v-if="boxErrors.employee_id" class="mt-1 text-xs text-red-500">
                    {{ boxErrors.employee_id }}
                  </p>
                </div>
              </div>
              <div class="mt-6">
                <button
                  class="mx-auto w-full max-w-3xl h-10 rounded-full flex items-center justify-center gap-2 text-white font-semibold disabled:opacity-70 disabled:cursor-not-allowed"
                  style="background-color: #5b9ac5"
                  :disabled="loadingBox"
                  @click="viewPaymentBoxReport"
                >
                  <svg
                    v-if="loadingBox"
                    class="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  View Report
                </button>
              </div>
            </div>

            <!-- Daily Report tab -->
            <div v-else>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-5 mx-auto max-w-3xl">
                <div>
                  <label class="block text-sm font-bold text-gray-700 mb-1">Company</label>
                  <select
                    v-model="daily.company_id"
                    class="w-full h-10 rounded-xl border-2 px-3 text-sm focus:outline-none"
                    :class="dailyErrors.company_id ? 'border-red-400' : 'border-gray-300'"
                    @change="clearDailyError('company_id')"
                  >
                    <option value="" disabled>Select company</option>
                    <option v-for="c in companies" :key="c.id" :value="c.id">{{ c.name }}</option>
                  </select>
                  <p v-if="dailyErrors.company_id" class="mt-1 text-xs text-red-500">
                    {{ dailyErrors.company_id }}
                  </p>
                </div>
                <div>
                  <label class="block text-sm font-bold text-gray-700 mb-1">Employee</label>
                  <select
                    v-model="daily.employee_id"
                    class="w-full h-10 rounded-xl border-2 px-3 text-sm focus:outline-none"
                    :class="dailyErrors.employee_id ? 'border-red-400' : 'border-gray-300'"
                    @change="clearDailyError('employee_id')"
                  >
                    <option value="all">All</option>
                    <option v-for="e in employees" :key="e.id" :value="e.id">{{ e.name }}</option>
                  </select>
                  <p v-if="dailyErrors.employee_id" class="mt-1 text-xs text-red-500">
                    {{ dailyErrors.employee_id }}
                  </p>
                </div>
              </div>
              <div class="mt-6">
                <button
                  class="mx-auto w-full max-w-3xl h-10 rounded-full flex items-center justify-center gap-2 text-white font-semibold disabled:opacity-70 disabled:cursor-not-allowed"
                  style="background-color: #5b9ac5"
                  :disabled="loadingDaily"
                  @click="viewDailyReport"
                >
                  <svg
                    v-if="loadingDaily"
                    class="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  View Report
                </button>
              </div>
            </div>
          </div>

          <div v-else class="text-center text-gray-500 py-8">
            You don't have permission to view reports.
          </div>
        </div>
      </div>
    </div>

    <DynamicForm
      v-if="showAddModal"
      :title="'Add New Payment'"
      :fields="formFields"
      :cols="3"
      v-model="formModel"
      actionLabel="Save"
      actionVariant="primary"
      @submit="submitNewPayment"
      @action="submitNewPayment"
      rowClass="text-lg"
    >
      <template #before-card>
        <div v-if="apiResponseText" class="mx-auto max-w-8xl mb-4">
          <div class="text-blue-900 border border-blue-200 rounded-xl px-4 py-3 text-sm">
            <div class="font-semibold mb-1">{{ apiResponseTitle }}</div>
            <div class="whitespace-pre-wrap">{{ apiResponseText }}</div>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="mx-auto max-w-8xl flex justify-end">
          <button
            type="button"
            class="h-9 px-4 rounded-full border border-gray-300 hover:bg-gray-50"
            @click="closeAddPaymentModal"
          >
            Close
          </button>
        </div>
      </template>
    </DynamicForm>

    <transition name="fade">
      <div
        v-if="showDeleteModal"
        class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        @click.self="cancelDelete"
        @keydown.esc="cancelDelete"
        tabindex="0"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-title"
      >
        <div class="bg-white rounded-[28px] w-full max-w-lg shadow-xl p-8 sm:p-10">
          <div
            class="mx-auto mb-6 w-16 h-16 rounded-full bg-red-500 grid place-items-center shadow-md"
          >
            <svg viewBox="0 0 24 24" class="w-7 h-7 text-white">
              <path
                fill="currentColor"
                d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.3 19.71 2.89 18.3 9.17 12 2.89 5.71 4.3 4.29 10.59 10.6 16.89 4.29z"
              />
            </svg>
          </div>
          <h2
            id="delete-title"
            class="text-center text-lg sm:text-[15px] leading-7 font-bold text-gray-900 mb-2"
          >
            Are you sure for
          </h2>
          <p class="text-center text-lg sm:text-[15px] leading-7 font-bold text-gray-900">
            delete this Payment
          </p>
          <div class="mt-8 flex items-center justify-between">
            <button
              type="button"
              class="h-10 px-6 rounded-full border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50"
              @click="cancelDelete"
            >
              Cancel
            </button>
            <button
              type="button"
              class="h-10 px-8 rounded-full text-white shadow-sm hover:opacity-95"
              :style="{ backgroundColor: '#5B9AC5' }"
              @click="confirmDelete"
              :disabled="deleting"
            >
              yes
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import api from '@/api'
import DynamicTable from '@/components/DynamicTable.vue'
import DynamicForm from '@/components/DynamicForm.vue'

export default {
  name: 'Payments',
  components: { DynamicTable, DynamicForm },

  data() {
    return {
      can: {
        view: false,
        viewPayments: false,
        viewPaymentBox: false,
        edit: false,
        delete: false,
        file: false,
        allowedCompanies: null,
      },
      showCompanyName: true,
      activeMainTab: 'payments',
      paymentBoxTab: 'box',
      showDeleteModal: false,
      deleting: false,
      rowToDelete: null,
      loadingBox: false,
      loadingDaily: false,
      fileLoadingId: null,
      boxErrors: {},
      dailyErrors: {},
      payments: [],
      pagination: { page: 1, perPage: 50, total: 0 },
      // Current search term applied to the Payments table (server-side).
      searchTerm: '',
      actions: [],
      filters: { from: '', to: '', company_id: '', employee_id: 'all' },
      daily: { company_id: '', employee_id: 'all' },
      companies: [],
      employees: [],
      showAddModal: false,
      formModel: {},
      companyMap: {
        1: 'شركة ليون لتأجير السيارات',
        2: 'شركة مارفيل لتأجير السيارات',
        3: 'شركة ليون للسياحة والسفر',
      },
      formFields: [
        {
          key: 'payment_for',
          label: 'Payment For',
          type: 'select',
          options: [
            { value: 'contracts', label: 'Contracts' },
            { value: 'companies', label: 'Companies' },
            { value: 'lawsuit', label: 'Lawsuit' },
            { value: 'sold_car', label: 'Sold Car' },
          ],
          props: { required: true },
        },
        {
          key: 'payment_for_id',
          label: 'Payment For Id',
          type: 'number',
          props: { required: true },
        },
        {
          key: 'payment_value',
          label: 'Payment Value',
          type: 'number',
          props: { required: true, step: '0.01', min: '0' },
        },
        { key: 'date', label: 'Date', type: 'date', props: { required: true } },
        { key: 'bank_id', label: 'Bank Name', type: 'select', options: [] },
        {
          key: 'payment_type',
          label: 'Payment Type',
          type: 'select',
          options: [
            { value: 'cash', label: 'Cash' },
            { value: 'credit_card', label: 'Credit Card' },
            { value: 'check', label: 'Check' },
            { value: 'bank_transfer', label: 'Bank Transfer' },
            { value: 'smart_link', label: 'Smart Link' },
          ],
          props: { required: true },
        },
      ],
      apiResponseTitle: '',
      apiResponseText: '',
    }
  },

  computed: {
    columns() {
      return [
        { key: 'id', label: 'Payment ID', width: '90px' },
        { key: 'contract_id', label: 'Contract ID', width: '90px' },
        { key: 'customer_name', label: 'Customer Name', width: '330px' },
        {
          key: 'company_name',
          label: 'Company',
          width: this.showCompanyName ? '220px' : '70px',
        },
        { key: 'payment_for', label: 'Payment For', width: '105px' },
        { key: 'payment_value', label: 'Payment Value', width: '105px' },
        { key: 'transfared_to_bank', label: 'Transfared to Bank', width: '100px' },
        { key: 'user_name', label: 'User Name', width: '140px' },
        { key: 'action', label: 'Action', width: '250px' },
      ]
    },
  },

  async created() {
    await this.ensurePermissions()
    if (!this.can.view) return

    if (this.can.viewPayments) this.activeMainTab = 'payments'
    else if (this.can.viewPaymentBox) this.activeMainTab = 'paymentBox'

    if (this.can.viewPayments) this.fetchPayments()
    if (this.can.viewPaymentBox) this.bootstrapFiltersData()
  },

  watch: {
    'pagination.page'(newPage) {
      if (this.can.viewPayments) this.fetchPayments(newPage)
    },
    activeMainTab(newTab) {
      if (!this.can.view) return
      if (newTab === 'payments' && this.can.viewPayments && !this.payments.length) {
        this.fetchPayments()
      }
      if (newTab === 'paymentBox' && this.can.viewPaymentBox) {
        this.bootstrapFiltersData()
      }
    },
  },

  methods: {
    authConfig() {
      const token = localStorage.getItem('token')
      return { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    },

    toggleCompanyCol() {
      this.showCompanyName = !this.showCompanyName
    },

    async ensurePermissions() {
      try {
        const res = await api.get('/accounting/permissions/nav', this.authConfig())
        const nav = res.data?.permissions || res.data || {}
        const pySection = nav?.['Payments']?.['Payments'] || {}
        const granted = pySection.granted || []

        const has = (perm) => granted.includes(perm)

        this.can.view = has('payments.payments.view')
        this.can.viewPayments = has('payments.payments.view')
        this.can.viewPaymentBox = has('payments.payments.view_payment_box')
        this.can.edit = has('payments.payments.edit')
        this.can.delete = has('payments.payments.delete')
        this.can.file = has('payments.payments.file')

        const prefix = 'payments.payments.payment_box.company.'
        const suffix = '.view'
        const pyAll = pySection.all || []

        const allCompanyPerms = pyAll.filter((p) => p.startsWith(prefix) && p.endsWith(suffix))
        const grantedCompanyPerms = granted.filter(
          (p) => p.startsWith(prefix) && p.endsWith(suffix),
        )

        if (allCompanyPerms.length === 0) {
          this.can.allowedCompanies = null
        } else {
          this.can.allowedCompanies = grantedCompanyPerms.map((p) =>
            p.slice(prefix.length, -suffix.length),
          )
        }
      } catch (err) {
        console.error('[Payments] ensurePermissions failed:', err)
        this.can = {
          view: false,
          viewPayments: false,
          viewPaymentBox: false,
          edit: false,
          delete: false,
          file: false,
          allowedCompanies: null,
        }
      }
    },

    subdomain() {
      return localStorage.getItem('subdomain')
    },

    switchBoxTab(tab) {
      this.paymentBoxTab = tab
      this.boxErrors = {}
      this.dailyErrors = {}
    },

    clearBoxError(field) {
      this.boxErrors = { ...this.boxErrors, [field]: null }
    },

    clearDailyError(field) {
      this.dailyErrors = { ...this.dailyErrors, [field]: null }
    },

    // Called when DynamicTable emits a (debounced) search term change.
    onSearchChange(value) {
      this.searchTerm = value
      if (this.pagination.page !== 1) {
        // Changing the page triggers the 'pagination.page' watcher above,
        // which calls fetchPayments(1) for us.
        this.pagination.page = 1
      } else {
        // Page was already 1, so the watcher above won't fire - fetch manually.
        this.fetchPayments(1)
      }
    },

    async fetchPayments(page = this.pagination.page) {
      if (!this.can.view) return
      try {
        const params = new URLSearchParams()
        params.append('page', page)
        params.append('per_page', this.pagination.perPage)
        if (this.searchTerm) params.append('search', this.searchTerm)

        const response = await api.get(`/accounting/payments?${params.toString()}`)
        const list = (response.data.payments || []).map((p) => ({
          id: p.id,
          contract_id: p.contract_ref ?? '-',
          customer_name: p.customer_name ?? '',
          payment_for: p.payment_for ?? '',
          payment_value: (p.payment_value ?? 0) + ' ' + (p.currency ?? 'JOD'),
          transfared_to_bank: p.transferred_to_bank ? 'yes' : 'no',
          user_name: p.payment_method === 'online' ? 'online' : (p.user_name ?? ''),
          payment_type: p.payment_method ?? '',
          company_name:
            p.company_name ?? this.companyMap[p.company_id] ?? `Company ${p.company_id}`,
          company_id: p.company_id ?? '',
        }))
        this.payments = list

        const meta = response.data.meta || {}
        this.pagination.page = meta.current_page ?? page
        this.pagination.total = meta.total ?? list.length
      } catch (error) {
        console.error('Error fetching payments:', error)
      }
    },

    onRowsPerPageChange(newPerPage) {
      this.pagination.perPage = newPerPage
      this.pagination.page = 1
      this.fetchPayments(1)
    },

    async bootstrapFiltersData() {
      if (!this.can.viewPaymentBox) return
      try {
        const [companiesRes, usersRes] = await Promise.all([
          api.get('/accounting/companies'),
          api.get('/accounting/users'),
        ])

        let allCompanies = (companiesRes.data.data || companiesRes.data || []).map((c) => ({
          id: c.id,
          name: c.name,
        }))

        if (this.can.allowedCompanies !== null) {
          allCompanies = allCompanies.filter((c) => this.can.allowedCompanies.includes(c.name))
        }

        this.companies = allCompanies

        this.employees = (usersRes.data.data || usersRes.data || []).map((u) => ({
          id: u.id,
          name: u.name,
        }))
      } catch (err) {
        console.warn('bootstrapFiltersData: could not load data.', err)
      }
    },

    closeAddPaymentModal() {
      this.showAddModal = false
    },

    async fetchPaymentForData(form) {
      try {
        const payload = { id: form.payment_for_id, type: form.payment_for }
        const response = await api.post('/cars/accounting/payments/PaymentForDetails', payload, {
          baseURL: `https://${this.subdomain()}.booking.lyon-jo.com/api`,
        })
        if (response.data.status === 'success') {
          this.apiResponseTitle = 'Payment for Details:'
          this.apiResponseText = this.generateApiResponse(payload.type, response.data)
        } else {
          this.apiResponseTitle = 'Not Found:'
          this.apiResponseText = 'No record found for this reference.'
        }
      } catch {
        this.apiResponseTitle = 'Error'
        this.apiResponseText = 'Failed to fetch data.'
      }
    },

    generateApiResponse(type, data) {
      switch (type) {
        case 'contracts':
        case 'companies':
          return `Customer Name: ${data.full_name} || Remaining Price: ${data.total_price}`
        case 'lawsuit':
          return `Lawsuit Type: ${data.type} || Lawyer Name: ${data.lawyer_name} || Company Name: ${data.company_name} || Lawsuit Date: ${data.date} || Remaining Price: ${data.total_price}`
        case 'sold_car':
          return `Buyer Name: ${data.buyer_name} || Car Number: ${data.car_number} || Remaining Price: ${data.total_price}`
        default:
          return 'Invalid data type.'
      }
    },

    async submitNewPayment() {
      try {
        const form = { ...this.formModel }
        if (
          !form.payment_for ||
          !form.payment_for_id ||
          !form.payment_value ||
          !form.date ||
          !form.payment_type
        ) {
          alert('Please fill all required fields.')
          return
        }
        await this.fetchPaymentForData(form)
        const keyMapping = {
          contracts: 'booking_id',
          companies: 'customer_id',
          lawsuit: 'lawsuits_id',
          sold_car: 'sold_car_id',
        }
        const formData = new FormData()
        for (const k in form) {
          let key = k
          if (k === 'payment_for_id' && form.payment_for) key = keyMapping[form.payment_for] || k
          const val = form[k]
          if (val !== null && val !== undefined) formData.append(key, val)
        }
        const resp = await api.post('/cars/accounting/payments/addNew', formData, {
          baseURL: `https://${this.subdomain()}.booking.lyon-jo.com/api`,
        })
        await this.fetchPayments()
        this.closeAddPaymentModal()
        console.log('Payment added:', resp?.data)
      } catch (error) {
        console.error('Error submitting payment:', error)
      }
    },

    editPayment(row) {
      if (!this.can.edit) return
      this.$router.push({
        name: 'PaymentAddEdit',
        state: {
          paymentMode: 'edit',
          paymentType: row.payment_for?.toLowerCase() || 'contracts',
          paymentId: row.id,
        },
      })
    },

    openDeleteModal(row) {
      if (!this.can.delete) return
      this.rowToDelete = row
      this.showDeleteModal = true
      document.body.style.overflow = 'hidden'
    },

    cancelDelete() {
      this.showDeleteModal = false
      this.rowToDelete = null
      this.deleting = false
      document.body.style.overflow = ''
    },

    async confirmDelete() {
      if (!this.rowToDelete) return
      this.deleting = true
      const id = this.rowToDelete.id
      try {
        await api.post(`/accounting/payments/delete/${id}`)
        await this.fetchPayments()
        this.cancelDelete()
      } catch (e) {
        console.error('delete payment failed:', e)
        this.deleting = false
      }
    },

    async openFile(row) {
      if (!this.can.file) return
      if (this.fileLoadingId) return
      this.fileLoadingId = row.id
      try {
        const params = new URLSearchParams()
        if (row.payment_type) params.append('payment_method', row.payment_type)
        const resp = await api.get(`/accounting/payments/${row.id}/file?${params.toString()}`, {
          responseType: 'blob',
        })
        window.open(URL.createObjectURL(resp.data), '_blank')
      } catch (e) {
        console.error('Failed to open file:', e)
      } finally {
        this.fileLoadingId = null
      }
    },

    rowClass(row) {
      return row.payment_type === 'credit_card' ? 'credit-card' : ''
    },

    validateBox() {
      const e = {}
      if (!this.filters.from) e.from = 'From date is required'
      if (!this.filters.to) e.to = 'To date is required'
      if (!this.filters.company_id) e.company_id = 'Company is required'
      if (!this.filters.employee_id) e.employee_id = 'Employee is required'
      this.boxErrors = e
      return Object.keys(e).length === 0
    },

    validateDaily() {
      const e = {}
      if (!this.daily.company_id) e.company_id = 'Company is required'
      if (!this.daily.employee_id) e.employee_id = 'Employee is required'
      this.dailyErrors = e
      return Object.keys(e).length === 0
    },

    async viewPaymentBoxReport() {
      if (!this.can.viewPaymentBox) return
      if (!this.validateBox()) return
      const params = new URLSearchParams()
      if (this.filters.from) params.append('from', this.filters.from)
      if (this.filters.to) params.append('to', this.filters.to)
      if (this.filters.company_id) params.append('company_id', this.filters.company_id)
      if (this.filters.employee_id && this.filters.employee_id !== 'all') {
        params.append('user_id', this.filters.employee_id)
        const emp = this.employees.find((e) => e.id === this.filters.employee_id)
        if (emp) params.append('user_name', emp.name)
      }
      this.loadingBox = true
      try {
        const resp = await api.get(`/accounting/payments/box/pdf?${params.toString()}`, {
          responseType: 'blob',
        })
        window.open(URL.createObjectURL(resp.data), '_blank')
      } catch (e) {
        console.error('Failed to load PDF:', e)
      } finally {
        this.loadingBox = false
      }
    },

    async viewDailyReport() {
      if (!this.can.viewPaymentBox) return
      if (!this.validateDaily()) return
      const params = new URLSearchParams()
      const today = new Date().toISOString().split('T')[0]
      params.append('date', today)
      if (this.daily.company_id) params.append('company_id', this.daily.company_id)
      if (this.daily.employee_id && this.daily.employee_id !== 'all') {
        params.append('user_id', this.daily.employee_id)
        const emp = this.employees.find((e) => e.id === this.daily.employee_id)
        if (emp) params.append('user_name', emp.name)
      }
      this.loadingDaily = true
      try {
        const resp = await api.get(`/accounting/payments/daily/pdf?${params.toString()}`, {
          responseType: 'blob',
        })
        window.open(URL.createObjectURL(resp.data), '_blank')
      } catch (e) {
        console.error('Failed to load PDF:', e)
      } finally {
        this.loadingDaily = false
      }
    },
  },
}
</script>

<style scoped>
:deep(td) {
  @apply text-base;
}
</style>
